import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Map, List, Calendar, Navigation, Loader, MapPin, Mail, Phone, CheckCircle, Wrench } from 'lucide-react';
import api from '../services/api';
import { serviceTypesApi, taxConfigApi } from '../services/api';
import { formatCurrency, formatDate, getStatusColor } from '../utils/helpers';
import { useBusinessContext } from '../contexts/BusinessContext';
import Loading from '../components/Loading';
import ConfirmModal from '../components/ConfirmModal';
import Toast from '../components/Toast';

// Extracted components
import JobCard from '../components/jobs/JobCard';
import JobFormModal from '../components/jobs/JobFormModal';
import CompleteJobModal from '../components/jobs/CompleteJobModal';
import JobDetailDrawer from '../components/jobs/JobDetailDrawer';

// NEW: Calendar components
import { CalendarView } from '../components/calendar';

// NEW: Multi-Day Route Analyzer (replaces old Route Planner)
import MultiDayAnalyzer from '../components/jobs/MultiDayAnalyzer';

// Helper: Get local date string in YYYY-MM-DD format (NOT UTC)
const getLocalDateString = (date = new Date()) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const getQuoteAge = (createdAt) => {
  const days = Math.floor((Date.now() - new Date(createdAt)) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return '1 week ago';
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} mo ago`;
};

// UPDATED: Smart Views for better job organization
const smartViews = [

  {
    id: 'today',
    label: 'Today\'s Work',
    icon: '🎯',
    description: 'Jobs scheduled for today',
    color: 'from-blue-500 to-blue-600',
    filter: (job) => {
      const today = new Date().toDateString();
      const jobDate = job.scheduledDate ? new Date(job.scheduledDate).toDateString() : null;
      return jobDate === today && ['Scheduled', 'In Progress'].includes(job.status);
    }
  },
  {
    id: 'needs-attention',
    label: 'Quoted',
    icon: '💬',
    description: 'All open quotes',
    color: 'from-amber-500 to-orange-600',
    filter: (job) => job.status === 'Quoted',
  },
  {
    id: 'ready-invoice',
    label: 'Ready to Invoice',
    icon: '💰', 
    description: 'Completed jobs not yet invoiced',
    color: 'from-green-500 to-emerald-600',
    filter: (job) => job.status === 'Completed' && !job.isInvoiced && job.transactionType !== 'Personal'
  },
  {
    id: 'personal',
    label: 'Personal Work',
    icon: '👥',
    description: 'Personal and family services',
    color: 'from-slate-500 to-slate-600',
    filter: (job) => job.transactionType === 'Personal'
  },
  {
    id: 'completed',
    label: 'Recently Completed',
    icon: '✅',
    description: 'Jobs completed in the last 7 days',
    color: 'from-purple-500 to-purple-600', 
    filter: (job) => {
      if (job.status !== 'Completed') return false;
      const daysSince = (Date.now() - new Date(job.completedDate || job.updatedAt)) / (1000 * 60 * 60 * 24);
      return daysSince <= 7;
    }
  },
  {
    id: 'scheduled',
    label: 'Scheduled',
    icon: '📅',
    description: 'All scheduled jobs',
    color: 'from-indigo-500 to-indigo-600',
    filter: (job) => job.status === 'Scheduled'
  },
  {
    id: 'all',
    label: 'All Jobs',
    icon: '📋',
    description: 'Every job in the system',
    color: 'from-gray-500 to-gray-600',
    filter: () => true
  }
];

const Jobs = () => {
  const { user, isSimpleMode } = useBusinessContext();
  const isOwner = user?.role === 'Owner';
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Core data
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // UPDATED: Smart view filter instead of status filter
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState(() => {
    const urlView = searchParams.get('view');
    if (urlView && smartViews.some(v => v.id === urlView)) {
      return urlView;
    }
    return 'today'; // Default to "Today's Work" instead of "all"
  });
  
  // View mode - Calendar is now default!
  const [viewMode, setViewMode] = useState(() => {
    const urlViewMode = searchParams.get('mode');
    if (urlViewMode === 'list' || urlViewMode === 'map') return urlViewMode;
    // If a status filter is in the URL, default to list view so results are visible
    const urlStatus = searchParams.get('status') || searchParams.get('view');
    if (urlStatus && urlStatus !== 'today') return 'list';
    return 'calendar';
  });
  
  // Calendar selected date (persists across edits)
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(() => getLocalDateString());
  
  // Modals
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completingJob, setCompletingJob] = useState(null);
  const [completeFormData, setCompleteFormData] = useState({
    finalAmount: '',
    completedDate: getLocalDateString(),
    notes: '',
    sendCompletedEmail: false,
    paidOnSite: false,
    paymentMethod: 'Cash',
    paymentReference: '',
    sendReceiptEmail: false,
    salesTaxCollected: false,
    transactionType: 'Business',
  });
  
  // Job Detail Drawer state
  const [selectedJob, setSelectedJob] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Email sending state
  const [sendingEmail, setSendingEmail] = useState(null);
  const [sendingQuoteEmail, setSendingQuoteEmail] = useState(null);
  const [sendingQuoteSms, setSendingQuoteSms] = useState(null);
  
  // Email settings
  const [emailSettings, setEmailSettings] = useState({
    sendJobConfirmation: false,
    sendServicePrep: false,
    sendJobReminder: false,
    sendJobCompleted: false,
    sendReviewRequest: false,
    sendPaymentReceived: false,
  });
  const [emailSettingsLoaded, setEmailSettingsLoaded] = useState(false);

  const [smsSettings, setSmsSettings] = useState({
  smsNotificationsEnabled: false,
  smsQuoteTextingEnabled: false,
});
  
  // Tax settings
  const [taxSettings, setTaxSettings] = useState({
    collectsSalesTax: false,
    salesTaxRate: 0,
  });

  // Route optimization state
  const [dayAnalyses, setDayAnalyses] = useState({});
  const [isReoptimizing, setIsReoptimizing] = useState(false);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger'
  });

  // Toast notifications (replaces alert())
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'error') => setToast({ message, type });
  const closeToast = useCallback(() => setToast(null), []);

  // Load initial data
  useEffect(() => {
    loadData();
    loadCustomers();
    loadServiceTypes();
    loadEmailSettings();
    loadTaxSettings();
    loadSmsSettings();
  }, []);

  // UPDATED: Filter jobs based on active smart view and search
  useEffect(() => {
    let filtered = jobs;
    
    // Apply smart view filter
    const currentView = smartViews.find(v => v.id === activeView);
    if (currentView) {
      filtered = filtered.filter(currentView.filter);
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(job => 
        job.customer?.name?.toLowerCase().includes(term) ||
        job.address?.toLowerCase().includes(term) ||
        job.city?.toLowerCase().includes(term) ||
        job.description?.toLowerCase().includes(term) ||
        job.jobServices?.some(js => js.serviceType?.name?.toLowerCase().includes(term))
      );
    }
    
    setFilteredJobs(filtered);
  }, [jobs, activeView, searchTerm]);

  // Update URL when view changes
  const handleViewChange = (newView) => {
    setActiveView(newView);
    searchParams.set('view', newView);
    setSearchParams(searchParams);
  };

  // Update URL when view mode changes
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    searchParams.set('mode', mode);
    setSearchParams(searchParams);
  };

  // API calls
  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/jobs');
      setJobs(response.data);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadServiceTypes = async () => {
  try {
    const response = await serviceTypesApi.getAll();
    setServiceTypes(response.data);
  } catch (error) {
    console.error('Error loading service types:', error);
  }
};

  const loadEmailSettings = async () => {
    try {
      const response = await api.get('/business/email-settings');
      setEmailSettings(response.data);
      setEmailSettingsLoaded(true);
    } catch (error) {
      console.error('Error loading email settings:', error);
      setEmailSettingsLoaded(true);
    }
  };

  const loadSmsSettings = async () => {
  try {
    const response = await api.get('/business/sms-settings');
    setSmsSettings({
      smsNotificationsEnabled: response.data.smsNotificationsEnabled || false,
      smsQuoteTextingEnabled: response.data.smsQuoteTextingEnabled || false,
    });
  } catch (error) {
    console.error('Error loading SMS settings:', error);
  }
};

  const loadTaxSettings = async () => {
  try {
    const response = await taxConfigApi.getConfiguration();
    if (response.data) {
      setTaxSettings({
        collectsSalesTax: response.data.collectsSalesTax || false,
        salesTaxRate: response.data.salesTaxRate || 0,
      });
    }
  } catch (error) {
    console.error('Error loading tax settings:', error);
  }
};

  // Get counts for each smart view
  const getViewCounts = () => {
    const counts = {};
    smartViews.forEach(view => {
      counts[view.id] = jobs.filter(view.filter).length;
    });
    return counts;
  };

  const viewCounts = getViewCounts();

  // Check if any email notifications are enabled
  const anyEmailsEnabled = Object.values(emailSettings).some(enabled => enabled);

  // Format full address
  const formatAddress = (job) => {
    const parts = [job.address, job.city, job.state, job.zipCode].filter(Boolean);
    return parts.join(', ');
  };

  // Get service names from job services
  const getServiceNames = (job) => {
    if (job.jobServices && job.jobServices.length > 0) {
      return job.jobServices
        .map(js => js.serviceType?.name)
        .filter(Boolean)
        .join(', ');
    }
    return null;
  };

  // Professional status badge styling for Personal jobs
  const getPersonalStatusColor = (job) => {
    const isPersonal = job.transactionType === 'Personal';
    if (isPersonal && job.status === 'Completed') {
      return 'bg-slate-600 text-white';
    }
    return getStatusColor(job.status);
  };

  // Professional status text
  const getPersonalStatusText = (job) => {
    const isPersonal = job.transactionType === 'Personal';
    if (isPersonal && job.status === 'Completed') {
      return 'Completed (Personal)';
    }
    return job.status;
  };

  // Check if prep email can be sent
  const canSendPrepEmail = (job) => {
    return job.status === 'Scheduled' && 
           !job.prepEmailSent && 
           job.scheduledDate && 
           job.customer?.email;
  };

  // Check if quote email can be sent (Owner only)
  const canSendQuoteEmail = (job) => {
    const hasAmount = job.quoteAmount > 0;
    const hasServices = job.jobServices && job.jobServices.length > 0;
    const hasEmail = job.customer?.email;
    
    return isOwner &&
           job.status === 'Quoted' && 
           !job.quoteEmailSent && 
           hasEmail &&
           hasAmount &&
           hasServices;
  };

  // Check if job can be completed (Scheduled or In Progress)
  const canComplete = (job) => {
    return job.status === 'Scheduled' || job.status === 'In Progress';
  };

  // Event handlers (keeping existing ones from your original code)
  const handleOpenNewJob = (initialData = {}) => {
    setEditingJob(null);
    setShowFormModal(true);
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setShowFormModal(true);
  };

  const handleDelete = (jobId, skipConfirm = false) => {
    const doDelete = async () => {
      try {
        await api.delete(`/jobs/${jobId}`);
        await loadData();
        setConfirmModal({ ...confirmModal, isOpen: false });
      } catch (error) {
        console.error('Error deleting job:', error);
        showToast('Failed to delete job');
      }
    };

    if (skipConfirm) {
      doDelete();
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Delete Job',
      message: 'Are you sure you want to delete this job? This action cannot be undone.',
      variant: 'danger',
      onConfirm: doDelete,
    });
  };

  const handleFormSubmit = async (submitData) => {
  try {
    const { formData, jobServices, totalQuoteAmount, emailOverrides, quoteBreakdownJson } = submitData;

    const payload = {
      customerId: formData.customerId,
      customerAssetId: formData.customerAssetId || null,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      status: formData.status,
      scheduledDate: formData.scheduledDate,
      quoteAmount: totalQuoteAmount,
      estimatedDurationMinutes: formData.estimatedDurationMinutes || null,
      description: formData.description,
      quoteBreakdownJson: quoteBreakdownJson,
      sendConfirmationEmail: emailOverrides?.sendConfirmationEmail || false,
      mileageIn: formData.mileageIn || null,
      behaviorNotes: formData.behaviorNotes || null,
      serviceFormula: formData.serviceFormula || null,
      // NOTE: No jobServices here - backend CreateJobDto doesn't have this property
    };

    const validServices = jobServices
      .filter(s => s.serviceTypeId)
      .map(s => ({
        serviceTypeId: parseInt(s.serviceTypeId),
        quantity: s.quantity || 1,
        unitPrice: s.unitPrice || null,
        totalPrice: s.totalPrice || 0,
        notes: s.notes || null,
        quoteBreakdownJson: s.quoteBreakdownJson || null,
      }));

    if (editingJob) {
      await api.put(`/jobs/${editingJob.id}`, { 
        ...payload, 
        id: editingJob.id, 
        businessId: editingJob.businessId 
      });

      if (!(editingJob.status === 'Completed' || editingJob.isInvoiced) && validServices.length > 0) {
        const editServiceTotal = validServices.reduce((sum, s) => sum + (s.totalPrice || 0), 0);
        const editSetPayload = { services: validServices };
        if (totalQuoteAmount !== editServiceTotal) {
          editSetPayload.overrideQuoteAmount = totalQuoteAmount;
        }
        await api.post(`/jobservices/job/${editingJob.id}/set`, editSetPayload);
      }
    } else {
      const response = await api.post('/jobs', payload);

      if (validServices.length > 0 && response.data?.id) {
        const serviceTotal = validServices.reduce((sum, s) => sum + (s.totalPrice || 0), 0);
        const setPayload = { services: validServices };
        if (totalQuoteAmount !== serviceTotal) {
          setPayload.overrideQuoteAmount = totalQuoteAmount;
        }
        await api.post(`/jobservices/job/${response.data.id}/set`, setPayload);
      }
    }

    await loadData();
    setShowFormModal(false);
    setEditingJob(null);
  } catch (error) {
    console.error('Error saving job:', error);
    throw error;
  }
};

  const handleReopen = (job) => {
    // Close the form first so the confirm dialog isn't stacked on top of it
    setShowFormModal(false);
    setEditingJob(null);
    setConfirmModal({
      isOpen: true,
      title: 'Reopen Job',
      message: `Reopen this job for ${job.customer?.name || 'this customer'}? It returns to In Progress so services and pricing can be edited, and you'll need to complete it again.`,
      confirmText: 'Reopen Job',
      variant: 'warning',
      onConfirm: async () => {
        try {
          await api.post(`/jobs/${job.id}/reopen`);
          await loadData();
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          showToast('Job reopened', 'success');
        } catch (error) {
          console.error('Error reopening job:', error);
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          showToast(error.response?.data?.message || 'Failed to reopen job');
        }
      },
    });
  };

  const handleOpenComplete = (job) => {
    setCompletingJob(job);
    setCompleteFormData({
      finalAmount: job.quoteAmount?.toString() || '',
      completedDate: getLocalDateString(),
      notes: job.notes || '',
      sendCompletedEmail: false,
      paidOnSite: false,
      paymentMethod: 'Cash',
      paymentReference: '',
      sendReceiptEmail: false,
      salesTaxCollected: false,
      transactionType: 'Business',
    });
    setShowCompleteModal(true);
  };

  const handleCompleteSubmit = async () => {
  if (!completingJob) return;
  
  try {
    const response = await api.post(`/jobs/${completingJob.id}/complete`, {
      finalAmount: completeFormData.finalAmount ? parseFloat(completeFormData.finalAmount) : null,
      completedDate: completeFormData.completedDate
        ? new Date(completeFormData.completedDate + 'T12:00:00').toISOString()
        : new Date().toISOString(),
      notes: completeFormData.notes || null,
      sendCompletedEmail: completeFormData.paidOnSite ? false : completeFormData.sendCompletedEmail,
      paidOnSite: completeFormData.paidOnSite || false,
      paymentMethod: completeFormData.paymentMethod || 'Cash',
      paymentReference: completeFormData.paymentReference || null,
      sendReceiptEmail: completeFormData.paidOnSite ? completeFormData.sendReceiptEmail : false,
      salesTaxCollected: completeFormData.paidOnSite ? (completeFormData.salesTaxCollected || false) : false,
      transactionType: completeFormData.transactionType || 'Business',
    });

    await loadData();

    // Simple mode: stay open on the success screen so the invoice can be sent or printed
    if (isSimpleMode && response.data?.invoiceId) {
      return response.data;
    }

    setShowCompleteModal(false);
    setCompletingJob(null);
  } catch (error) {
    console.error('Error completing job:', error);
    throw error;
  }
};

  const handleCardClick = (job) => {
    setSelectedJob(job);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedJob(null);
  };

  const handleSendPrepEmail = async (jobId) => {
    setSendingEmail(jobId);
    try {
      await api.post(`/jobs/${jobId}/send-prep-email`);
      await loadData();
      // Update selectedJob in drawer with fresh data
      if (selectedJob?.id === jobId) {
        const refreshed = await api.get(`/jobs/${jobId}`);
        setSelectedJob(refreshed.data);
      }
    } catch (error) {
      console.error('Error sending prep email:', error);
      showToast('Failed to send prep email');
    } finally {
      setSendingEmail(null);
    }
  };

  const handleSendQuoteEmail = async (jobId) => {
    setSendingQuoteEmail(jobId);
    try {
      await api.post(`/jobs/${jobId}/send-quote-email`);
      await loadData();
      // Update selectedJob in drawer with fresh data
      if (selectedJob?.id === jobId) {
        const refreshed = await api.get(`/jobs/${jobId}`);
        setSelectedJob(refreshed.data);
      }
    } catch (error) {
      console.error('Error sending quote email:', error);
      showToast('Failed to email quote');
    } finally {
      setSendingQuoteEmail(null);
    }
  };

  const handleSendQuoteSms = async (jobId) => {
  setSendingQuoteSms(jobId);
  try {
    await api.post(`/jobs/${jobId}/send-quote-sms`);
    await loadData();
    if (selectedJob?.id === jobId) {
      const refreshed = await api.get(`/jobs/${jobId}`);
      setSelectedJob(refreshed.data);
    }
  } catch (error) {
    console.error('Error sending quote SMS:', error);
    showToast('Failed to send quote text');
  } finally {
    setSendingQuoteSms(null);
  }
};

  // Calendar-specific handlers
  const handleCalendarJobClick = (job) => {
    handleCardClick(job);
  };

  const handleCalendarJobNavigate = (job) => {
    if (job.latitude && job.longitude) {
      const url = `https://maps.google.com/?q=${job.latitude},${job.longitude}`;
      window.open(url, '_blank');
    } else if (job.address) {
      const address = `${job.address}, ${job.city}, ${job.state} ${job.zipCode}`;
      const url = `https://maps.google.com/?q=${encodeURIComponent(address)}`;
      window.open(url, '_blank');
    }
  };

  const handleCalendarJobCall = (job) => {
    if (job.customer?.phone) {
      window.location.href = `tel:${job.customer.phone}`;
    }
  };

  const handleJobReschedule = (job) => {
    handleEdit(job);
  };

  const handleNavigateToNext = () => {
    console.log('Navigate to next job');
  };

  const handleReoptimizeToday = () => {
    console.log('Reoptimize today');
  };

  // NEW: Enhanced Desktop Job Card Component
  const DesktopJobCard = ({ job }) => {
    const isPersonal = job.transactionType === 'Personal';
    const serviceNames = getServiceNames(job);
    const showCompleteButton = canComplete(job);
const showTextQuoteButton = isOwner && job.status === 'Quoted' && !job.quoteSmsSent && job.customer?.phone && job.quoteAmount > 0 && job.jobServices?.length > 0 && smsSettings?.smsQuoteTextingEnabled && smsSettings?.smsNotificationsEnabled;

    return (
      <div 
        className={`card p-0 overflow-hidden cursor-pointer hover:shadow-lg transition-all ${
          isPersonal 
            ? 'bg-slate-50/50 border-gray-200 border-l-4 border-l-slate-700' 
            : ''
        }`}
        onClick={() => handleCardClick(job)}
      >
        <div className="grid lg:grid-cols-3 gap-6 p-6">
          {/* Left: Customer & Location */}
          <div className="lg:col-span-1">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-600 font-semibold text-lg">
                  {job.customer?.name?.charAt(0) || '?'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 text-lg mb-1">
                  {job.customer?.name || 'Unknown Customer'}
                </h3>
                <p className="text-sm text-gray-500 mb-2">{formatAddress(job)}</p>
                
                {/* Personal indicator */}
                {isPersonal && (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium text-slate-700">Personal/Family</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Middle: Job Details */}
          <div className="lg:col-span-1">
            <div className="space-y-3">
              {/* Date */}
              {job.scheduledDate && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar size={18} />
                  <span className="font-medium">{formatDate(job.scheduledDate)}</span>
                </div>
              )}
              
              {/* Services */}
              {serviceNames && (
                <div className="flex items-start gap-2 text-gray-600">
                  <Wrench size={18} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{serviceNames}</span>
                    {job.jobServices?.length > 1 && (
                      <span className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded-full">
                        +{job.jobServices.length - 1} more
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Status badges */}
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPersonalStatusColor(job)}`}>
                  {getPersonalStatusText(job)}
                </span>
                {job.status === 'Quoted' && (
                  <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-500">
                    {getQuoteAge(job.createdAt)}
                  </span>
                )}
                
                {job.isInvoiced && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    isPersonal 
                      ? 'bg-slate-200 text-slate-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {isPersonal ? 'Recorded ($0)' : 'Invoiced'}
                  </span>
                )}
                
                {job.prepEmailSent && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                    Prep Email Sent
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right: Amount & Actions */}
          <div className="lg:col-span-1">
            <div className="flex flex-col justify-between h-full">
              {/* Amount */}
              {isOwner && (
                <div className="text-right mb-4">
                  {isPersonal && job.quoteAmount > 0 ? (
                    <div>
                      <div className="text-gray-500 line-through text-sm mb-2">
                        {formatCurrency(job.quoteAmount)}
                      </div>
                      <div className="text-lg font-bold text-slate-700 px-3 py-2 bg-slate-200 rounded-lg inline-block">
                        No Charge
                      </div>
                    </div>
                  ) : job.quoteAmount > 0 ? (
                    <div className="text-2xl font-bold text-primary-600">
                      {formatCurrency(job.quoteAmount)}
                    </div>
                  ) : null}
                </div>
              )}
              
              {/* Quick Actions */}
              <div className="flex flex-col gap-2">
                {canSendQuoteEmail(job) && (
  <button 
    onClick={(e) => {
      e.stopPropagation();
      handleSendQuoteEmail(job.id);
    }}
    className="btn bg-purple-600 hover:bg-purple-700 text-white text-sm py-2"
  >
    Email Quote
  </button>
)}
{showTextQuoteButton && (
  <button 
    onClick={(e) => {
      e.stopPropagation();
      handleSendQuoteSms(job.id);
    }}
    disabled={sendingQuoteSms === job.id}
    className="btn bg-purple-700 hover:bg-purple-800 text-white text-sm py-2 disabled:opacity-50"
  >
    {sendingQuoteSms === job.id ? 'Sending...' : 'Text Quote'}
  </button>
)}
                {canSendPrepEmail(job) && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendPrepEmail(job.id);
                    }}
                    className="btn btn-primary text-sm py-2"
                  >
                    Prep Email
                  </button>
                )}
                {showCompleteButton && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenComplete(job);
                    }}
                    className="btn bg-green-600 hover:bg-green-700 text-white text-sm py-2 flex items-center justify-center gap-2"
                  >
                    <CheckCircle size={16} />
                    Complete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && jobs.length === 0) {
    return <Loading />;
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Jobs</h1>
          <p className="text-gray-600">{jobs.length} total jobs</p>
        </div>
        {isOwner && (
          <button
            onClick={handleOpenNewJob}
            className="btn btn-primary flex items-center gap-2 mt-4 sm:mt-0"
          >
            <Plus size={18} />
            New Job
          </button>
        )}
      </div>

      {/* View Mode Selector */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        <button
          onClick={() => handleViewModeChange('calendar')}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
            viewMode === 'calendar'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Calendar size={16} />
          Calendar
        </button>
        <button
          onClick={() => handleViewModeChange('list')}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
            viewMode === 'list'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <List size={16} />
          List
        </button>
        <button
          onClick={() => handleViewModeChange('map')}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
            viewMode === 'map'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Map size={16} />
          Week Planner
        </button>
      </div>

      {/* ============================================ */}
      {/* CALENDAR VIEW */}
      {/* ============================================ */}
      {viewMode === 'calendar' && (
        <div>
          <CalendarView
            jobs={jobs.filter(j => j.status === 'Scheduled' || j.status === 'In Progress' || j.status === 'Quoted')}
            onJobClick={handleCalendarJobClick}
            onJobComplete={handleOpenComplete}
            onJobEdit={handleEdit}
            onJobNavigate={handleCalendarJobNavigate}
            onJobCall={handleCalendarJobCall}
            onJobReschedule={handleJobReschedule}
            onNewJob={isOwner ? handleOpenNewJob : null}
            isOwner={isOwner}
            loading={loading}
            initialDate={calendarSelectedDate}
            onDateChange={setCalendarSelectedDate}
          />
        </div>
      )}

      {/* ============================================ */}
      {/* LIST VIEW - ENHANCED FOR DESKTOP */}
      {/* ============================================ */}
      {viewMode === 'list' && (
        <>
          {/* Desktop: Enhanced Stats Overview */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-6 mb-8">
            {smartViews.slice(0, 4).map((view) => {
              const count = viewCounts[view.id] || 0;
              const isActive = activeView === view.id;
              
              return (
                <button
                  key={view.id}
                  onClick={() => handleViewChange(view.id)}
                  className={`bg-gradient-to-br ${view.color} rounded-xl p-6 text-white text-left transition-all transform ${
                    isActive 
                      ? 'ring-4 ring-white ring-opacity-60 scale-105 shadow-xl' 
                      : 'opacity-40 hover:opacity-80 hover:scale-105 hover:shadow-lg'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center text-2xl">
                      {view.icon}
                    </div>
                    <span className="text-3xl font-bold">{count}</span>
                  </div>
                  <h3 className="font-semibold mb-1">{view.label}</h3>
                  <p className="text-white/80 text-sm">{view.description}</p>
                </button>
              );
            })}
          </div>

          {/* Desktop: Secondary filters */}
          <div className="hidden lg:flex items-center gap-3 mb-6">
            {smartViews.slice(4).map((view) => {
              const count = viewCounts[view.id] || 0;
              const isActive = activeView === view.id;
              
              return (
                <button
                  key={view.id}
                  onClick={() => handleViewChange(view.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span>{view.icon}</span>
                  {view.label}
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Mobile: Compact filter pills */}
          <div className="lg:hidden flex items-center gap-2 overflow-x-auto pb-3 mb-4">
            <Filter size={16} className="text-gray-400 flex-shrink-0" />
            {smartViews.map((view) => {
              const count = viewCounts[view.id] || 0;
              const isActive = activeView === view.id;
              
              return (
                <button
                  key={view.id}
                  onClick={() => handleViewChange(view.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span>{view.icon}</span>
                  {view.label}
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search customers, addresses, services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-9 py-3 text-sm w-full"
              />
            </div>
          </div>

          {/* Email Legend */}
          {anyEmailsEnabled && emailSettingsLoaded && (
            <div className="mb-4 flex items-center gap-2 text-xs text-gray-500">
              <Mail size={12} className="text-blue-500" />
              <span>= Status change may send customer email</span>
              {isOwner && (
                <a href="/settings" className="text-primary-600 hover:underline ml-1">
                  (Manage in Settings)
                </a>
              )}
            </div>
          )}

          {/* Job List */}
          {filteredJobs.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-gray-500">
                {searchTerm || activeView !== 'all'
                  ? 'No jobs found matching your filters' 
                  : 'No jobs yet. Create your first job!'}
              </p>
              {activeView === 'today' && viewCounts.all > 0 && (
                <button
                  onClick={() => handleViewChange('all')}
                  className="mt-2 text-primary-600 hover:text-primary-700 text-sm underline"
                >
                  View all {viewCounts.all} jobs
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredJobs.map((job) => (
                // Desktop: Use enhanced card, Mobile: Use original
                <div key={job.id} className="hidden lg:block">
                  <DesktopJobCard job={job} />
                </div>
              ))}
              
              {/* Mobile: Original JobCards */}
              <div className="lg:hidden space-y-3">
  {filteredJobs.map((job) => (
    <JobCard
      key={job.id}
      job={job}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onSendPrepEmail={handleSendPrepEmail}
      onSendQuoteEmail={handleSendQuoteEmail}
      onSendQuoteSms={handleSendQuoteSms}
      onComplete={handleOpenComplete}
      onCardClick={handleCardClick}
      sendingEmail={sendingEmail}
      sendingQuoteEmail={sendingQuoteEmail}
      sendingQuoteSms={sendingQuoteSms}
      smsSettings={smsSettings}
    />
  ))}
</div>
            </div>
          )}
        </>
      )}

      {/* ============================================ */}
      {/* WEEK PLANNER VIEW (Multi-Day Analyzer) */}
      {/* ============================================ */}
      {viewMode === 'map' && (
        <MultiDayAnalyzer
          jobs={jobs.filter(j => j.status === 'Scheduled' || j.status === 'In Progress')}
          onDayClick={(date) => {
            setCalendarSelectedDate(date);
            handleViewModeChange('calendar');
          }}
          onJobClick={(job) => {
            handleCardClick(job);
          }}
          onNewJob={isOwner ? handleOpenNewJob : null}
          isOwner={isOwner}
        />
      )}

      {/* ============================================ */}
      {/* SHARED MODALS & DRAWERS */}
      {/* ============================================ */}

      {/* Job Detail Drawer */}
      <JobDetailDrawer
  isOpen={drawerOpen}
  onClose={handleDrawerClose}
  job={selectedJob}
  onEdit={(job) => {
    handleDrawerClose();
    handleEdit(job);
  }}
  onDelete={(jobId) => {
    handleDrawerClose();
    handleDelete(jobId, true);
  }}
  onComplete={(job) => {
    handleDrawerClose();
    handleOpenComplete(job);
  }}
  onSendPrepEmail={handleSendPrepEmail}
  onSendQuoteEmail={handleSendQuoteEmail}
  onSendQuoteSms={handleSendQuoteSms}
  sendingEmail={sendingEmail}
  sendingQuoteEmail={sendingQuoteEmail}
  sendingQuoteSms={sendingQuoteSms}
  smsSettings={smsSettings}
/>

      {/* Job Form Modal */}
      <JobFormModal
        isOpen={showFormModal}
        onClose={() => { setShowFormModal(false); setEditingJob(null); }}
        onSubmit={handleFormSubmit}
        editingJob={editingJob}
        customers={customers}
        serviceTypes={serviceTypes}
        emailSettings={emailSettings}
        isOwner={isOwner}
        onReopen={handleReopen}
        onNotify={showToast}
      />

      {/* Complete Job Modal */}
      <CompleteJobModal
        isOpen={showCompleteModal}
        onClose={() => { setShowCompleteModal(false); setCompletingJob(null); loadData(); }}
        job={completingJob}
        formData={completeFormData}
        setFormData={setCompleteFormData}
        onSubmit={handleCompleteSubmit}
        emailSettings={emailSettings}
        taxSettings={taxSettings}
        remainingJobsToday={jobs.filter(j => {
          if (j.status !== 'Scheduled' && j.status !== 'In Progress') return false;
          const jobDate = j.scheduledDate?.split('T')[0];
          const todayStr = getLocalDateString();
          return jobDate === todayStr && j.id !== completingJob?.id;
        })}
        optimizedRoute={dayAnalyses[getLocalDateString()]}
        onNavigateToNext={handleNavigateToNext}
        onReoptimize={handleReoptimizeToday}
        isReoptimizing={isReoptimizing}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}

      {/* Confirm Modal */}
      <ConfirmModal
        confirmText={confirmModal.confirmText}
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
      />
    </div>
  );
};

export default Jobs;